import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { PrimaryButton } from "../buttons/PrimaryButton";
// import { getWeekRange } from "../../services/dateService";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";

dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

// Suppress ResizeObserver errors
if (typeof window !== "undefined") {
  const resizeObserverLoopErr = (e: ErrorEvent) => {
    if (
      e.message ===
        "ResizeObserver loop completed with undelivered notifications." ||
      e.message === "ResizeObserver loop limit exceeded"
    ) {
      e.stopImmediatePropagation();
      return false;
    }
  };
  window.addEventListener("error", resizeObserverLoopErr);
}

interface ICumulativeUsersDataPoint {
  period: string;
  date: string;
  cumulative_downloads: number;
  cumulative_droppers: number;
  cumulative_5plus: number;
  week_start?: string;
  week_end?: string;
  year?: number;
  quarter?: string;
}

interface PlasticCumulativeUsersProps {
  cumulativeUsersData: ICumulativeUsersDataPoint[] | null;
  isLoading: boolean;
  period: "weekly" | "monthly" | "quarterly" | "yearly";
  setPeriod: (period: "weekly" | "monthly" | "quarterly" | "yearly") => void;
  // Monthly: year, fromMonth, toMonth
  year: number;
  setYear: (year: number) => void;
  fromMonth: number;
  setFromMonth: (month: number) => void;
  toMonth: number;
  setToMonth: (month: number) => void;
  // Weekly: fromYear, fromWeek, toYear, toWeek
  fromYear: number;
  setFromYear: (year: number) => void;
  fromWeek: number;
  setFromWeek: (week: number) => void;
  toYear: number;
  setToYear: (year: number) => void;
  toWeek: number;
  setToWeek: (week: number) => void;
  // Quarterly: only year (shared with monthly)
  // Yearly: fromYear, toYear (shared with weekly)
}

export const PlasticCumulativeDroppers = React.memo(
  ({
    cumulativeUsersData,
    isLoading,
    period,
    setPeriod,
    year,
    setYear,
    fromMonth,
    setFromMonth,
    toMonth,
    setToMonth,
    fromYear,
    setFromYear,
    fromWeek,
    setFromWeek,
    toYear,
    setToYear,
    toWeek,
    setToWeek,
  }: PlasticCumulativeUsersProps) => {
    const snapshotRef = useRef<HTMLDivElement | null>(null);
    const [, takeScreenshot] = useScreenshot();

    // Year options (2024 to current year)
    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );
    const fromYearOptions = yearOptions;
    const toYearOptions = yearOptions;

    const monthOptions = useMemo(
      () => [
        { value: 1, label: "Jan" },
        { value: 2, label: "Feb" },
        { value: 3, label: "Mar" },
        { value: 4, label: "Apr" },
        { value: 5, label: "May" },
        { value: 6, label: "Jun" },
        { value: 7, label: "Jul" },
        { value: 8, label: "Aug" },
        { value: 9, label: "Sep" },
        { value: 10, label: "Oct" },
        { value: 11, label: "Nov" },
        { value: 12, label: "Dec" },
      ],
      []
    );
    const fromMonthOptions = useMemo(() => {
      const cy = new Date().getFullYear();
      const cm = new Date().getMonth() + 1;
      return fromYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [fromYear, monthOptions]);
    const toMonthOptions = useMemo(() => {
      const cy = new Date().getFullYear();
      const cm = new Date().getMonth() + 1;
      return toYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [toYear, monthOptions]);

    const getWeekOptions = (y: number) => {
      const last = dayjs(`${y}-12-31`);
      const totalWeeks = last.isoWeek() === 1 ? 52 : last.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();
      return Array.from({ length: totalWeeks }, (_, i) => {
        const w = i + 1;
        const s = dayjs().year(y).isoWeek(w).startOf("isoWeek");
        const e = dayjs().year(y).isoWeek(w).endOf("isoWeek");
        return {
          value: w,
          label: `Week ${w}: ${s.format("MMM DD")} - ${e.format("MMM DD")}`,
          disabled: y === currentYear && w > currentWeek,
        };
      });
    };
    const fromWeekOptions = useMemo(() => getWeekOptions(fromYear), [fromYear]);
    const toWeekOptions = useMemo(() => getWeekOptions(toYear), [toYear]);
    const downloadScreenshot = () => {
      if (!snapshotRef.current) return;
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "plastic_cumulative_droppers.png";
        link.click();
      });
    };

    // Transform API data into chart series
    const {
      categories,
      totalDroppers,
      materialDroppers,
      plasticDroppers,
      activePlasticDroppers,
    } = useMemo(() => {
      if (!cumulativeUsersData || cumulativeUsersData.length === 0) {
        return {
          categories: [],
          totalDroppers: [],
          materialDroppers: [],
          plasticDroppers: [],
          activePlasticDroppers: [],
        };
      }

      const cats: string[] = [];
      const total: number[] = [];
      const material: number[] = [];
      const plastic: number[] = [];
      const activePlastic: number[] = [];

      cumulativeUsersData.forEach((point: any) => {
        // Format date based on period
        let label = "";
        if (period === "weekly" && point.week_start) {
          label = dayjs(point.week_start).format("MMM DD");
        } else if (period === "monthly") {
          label = dayjs(point.date).format("YYYY-MM");
        } else if (period === "quarterly") {
          label = `${point.year} ${point.quarter}`;
        } else if (period === "yearly") {
          label = String(point.year);
        }
        cats.push(label);
        total.push(point.cumulative_droppers || 0);
        material.push(point.cumulative_droppers_material || 0);
        plastic.push(point.cumulative_droppers_plastic || 0);
        activePlastic.push(point.cumulative_active_plastic || 0);
      });

      return {
        categories: cats,
        totalDroppers: total,
        materialDroppers: material,
        plasticDroppers: plastic,
        activePlasticDroppers: activePlastic,
      };
    }, [cumulativeUsersData, period]);

    // Build nested stacked layers (active -> plastic -> material -> total)
    const activeLayer = activePlasticDroppers;
    const plasticMinusActive = plasticDroppers.map((val, idx) =>
      Math.max(0, val - (activePlasticDroppers[idx] || 0))
    );
    const materialMinusPlastic = materialDroppers.map((val, idx) =>
      Math.max(0, val - (plasticDroppers[idx] || 0))
    );
    const totalMinusMaterial = totalDroppers.map((val, idx) =>
      Math.max(0, val - (materialDroppers[idx] || 0))
    );

    const layeredSeriesMeta = [
      {
        name: "Active Plastic Droppers",
        data: activeLayer,
        color: "#FE7349",
      },
      {
        name: "Plastic Droppers (non-active)",
        data: plasticMinusActive,
        color: "#F8A991",
      },
      {
        name: "Material Droppers (non-plastic)",
        data: materialMinusPlastic,
        color: "#BFDFF7",
      },
      {
        name: "Other Droppers",
        data: totalMinusMaterial,
        color: "#19A1B9",
      },
    ];
    const series = layeredSeriesMeta.map(({ name, data }) => ({ name, data }));

    const yAxisMax = useMemo(() => {
      const maxVal = Math.max(
        ...totalDroppers,
        ...materialDroppers,
        ...plasticDroppers,
        ...activePlasticDroppers,
        0
      );
      if (!Number.isFinite(maxVal) || maxVal <= 0) return undefined;
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
      return Math.ceil((maxVal * 1.05) / magnitude) * magnitude;
    }, [
      totalDroppers,
      materialDroppers,
      plasticDroppers,
      activePlasticDroppers,
    ]);

    const options: ApexOptions = {
      chart: {
        id: "plastic-cumulative-droppers",
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        offsetX: 0,
        offsetY: 0,
      },
      title: {
        align: "left",
        style: { fontSize: "20px", fontWeight: 600 },
      },
      colors: layeredSeriesMeta.map((s) => s.color),
      fill: {
        type: "solid",
        opacity: 0.85,
      },
      dataLabels: { enabled: false },
      stroke: {
        width: 3,
        curve: "smooth" as const,
        colors: layeredSeriesMeta.map((s) => s.color),
      },
      grid: {
        strokeDashArray: 5,
        padding: {
          right: 30,
          left: 10,
        },
      },
      markers: { size: 1 },
      xaxis: {
        categories,
        tickAmount: categories.length - 1,
        labels: {
          rotate: -45,
          trim: false,
          style: {
            cssClass: "apexcharts-xaxis-label",
          },
        },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        labels: {
          formatter: (value) => Number(value).toLocaleString(),
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        markers: { size: 2 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ dataPointIndex }: any) {
          const rows = [
            {
              name: "Total Droppers",
              value: totalDroppers[dataPointIndex],
              color: "#19A1B9",
            },
            {
              name: "Material Droppers (excl. statie only)",
              value: materialDroppers[dataPointIndex],
              color: "#BFDFF7",
            },
            {
              name: "Plastic Droppers",
              value: plasticDroppers[dataPointIndex],
              color: "#F8A991",
            },
            {
              name: "Active Plastic Droppers",
              value: activePlasticDroppers[dataPointIndex],
              color: "#FE7349",
            },
          ].sort((a, b) => b.value - a.value);

          let html =
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
            (categories?.[dataPointIndex] ?? "") +
            "</div>";

          rows.forEach((item) => {
            html += `
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
              <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                <div class="apexcharts-tooltip-y-group">
                  <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                  <span class="apexcharts-tooltip-text-y-value">${item.value.toLocaleString()}</span>
                </div>
              </div>
            </div>`;
          });

          return html;
        },
      },
      annotations: {
        points: [
          {
            x: categories[categories.length - 1],
            y: totalDroppers[totalDroppers.length - 1],
            marker: { size: 0 },
            label: {
              text: `${totalDroppers[totalDroppers.length - 1]?.toLocaleString?.() || ""}`,
              offsetX: 12,
              borderColor: "#19A1B9",
              style: { background: "#F9FAFB", color: "#19A1B9" },
            },
          },
        ],
      },
    };

    return (
      <div className="flex flex-col w-full max-w-full overflow-hidden rounded-xl p-4 sm:p-6 bg-white border-gray-border drop-shadow-none mb-6">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>{" "}
          <div className="flex flex-col sm:flex-row items-center justify-start sm:justify-end gap-4">
            {/* Period */}
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                Period:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {period === "weekly"
                      ? "Weekly"
                      : period === "monthly"
                        ? "Monthly"
                        : period === "quarterly"
                          ? "Quarterly"
                          : "Yearly"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  <MenuItem
                    disabled={period === "weekly"}
                    onClick={() => setPeriod("weekly")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Weekly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "monthly"}
                    onClick={() => setPeriod("monthly")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Monthly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "quarterly"}
                    onClick={() => setPeriod("quarterly")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Quarterly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "yearly"}
                    onClick={() => setPeriod("yearly")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Yearly
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>

            {/* Monthly: From Year/Month + To Year/Month */}
            {period === "monthly" && (
              <>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    From:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {fromYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {fromYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={fromYear === y}
                          onClick={() => setFromYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {fromMonthOptions.find(
                          (m: any) => m.value === fromMonth
                        )?.label || "Month"}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {fromMonthOptions.map((m: any) => (
                        <MenuItem
                          key={m.value}
                          disabled={fromMonth === m.value}
                          onClick={() => setFromMonth(m.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {m.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    To:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {toYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {toYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={toYear === y}
                          onClick={() => setToYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {toMonthOptions.find((m: any) => m.value === toMonth)
                          ?.label || "Month"}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {toMonthOptions.map((m: any) => (
                        <MenuItem
                          key={m.value}
                          disabled={toMonth === m.value}
                          onClick={() => setToMonth(m.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {m.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
              </>
            )}

            {/* Weekly: From Year + From Week + To Year + To Week */}
            {period === "weekly" && (
              <>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    From:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {fromYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {fromYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={fromYear === y}
                          onClick={() => setFromYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu>
                    <MenuHandler>
                      <button
                        className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                        title={
                          fromWeekOptions.find((w: any) => w.value === fromWeek)
                            ?.label
                        }
                      >
                        {fromWeekOptions.find((w: any) => w.value === fromWeek)
                          ?.label || "Week"}
                        <ChevronDown className="ml-2 flex-shrink-0" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      className="rounded-none max-h-[250px] overflow-y-auto"
                      placeholder={undefined}
                    >
                      {fromWeekOptions.map((w: any) => (
                        <MenuItem
                          key={w.value}
                          disabled={w.disabled || fromWeek === w.value}
                          onClick={() => setFromWeek(w.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {w.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    To:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {toYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {toYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={toYear === y}
                          onClick={() => setToYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Menu>
                    <MenuHandler>
                      <button
                        className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                        title={
                          toWeekOptions.find((w: any) => w.value === toWeek)
                            ?.label
                        }
                      >
                        {toWeekOptions.find((w: any) => w.value === toWeek)
                          ?.label || "Week"}
                        <ChevronDown className="ml-2 flex-shrink-0" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      className="rounded-none max-h-[250px] overflow-y-auto"
                      placeholder={undefined}
                    >
                      {toWeekOptions.map((w: any) => (
                        <MenuItem
                          key={w.value}
                          disabled={w.disabled || toWeek === w.value}
                          onClick={() => setToWeek(w.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {w.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
              </>
            )}

            {/* Quarterly: Only Year (no From/To) */}
            {period === "quarterly" && (
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  Year:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {year}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((y: number) => (
                      <MenuItem
                        key={y}
                        disabled={year === y}
                        onClick={() => setYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            )}

            {/* Yearly: From Year + To Year */}
            {period === "yearly" && (
              <>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    From:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {fromYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {fromYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={fromYear === y}
                          onClick={() => setFromYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-satoshi text-sm text-gray-text">
                    To:
                  </label>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {toYear}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {toYearOptions.map((y: number) => (
                        <MenuItem
                          key={y}
                          disabled={toYear === y}
                          onClick={() => setToYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </div>
              </>
            )}

            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef} className="mt-4">
          <div className="flex items-center ">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Cumulative droppers over time
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-[420px]">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : !cumulativeUsersData || cumulativeUsersData.length === 0 ? (
            <div className="flex items-center justify-center h-[420px]">
              <p className="text-gray-500">No data available</p>
            </div>
          ) : (
            <Chart
              type="area"
              options={options}
              series={series as any}
              height={420}
            />
          )}
        </div>
        <div className="text-center text-xs text-gray-500 mt-2">
          {period === "weekly"
            ? "Week"
            : period === "monthly"
              ? "Month"
              : period === "quarterly"
                ? "Quarter"
                : "Year"}
        </div>
      </div>
    );
  }
);
